import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Package, Calendar, Tag, CheckCircle2, XCircle, FileText, ArrowRight, MousePointerClick, AlertTriangle, FileText as Page, ExternalLink, Video,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ServiceHero, { Highlight } from "./ServiceHero";
import IncludesExcludes from "./IncludesExcludes";

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setIsVisible(true); obs.unobserve(el); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, isVisible };
}
function AnimateIn({ children, className = "", delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? "translateY(0)" : "translateY(30px)",
      transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
    }}>{children}</div>
  );
}

const includesList = [
  "1 landing page de una sola página (one-page) orientada a conversión",
  "Diseño adaptado a tu identidad visual",
  "Maquetación responsive (móvil, tablet y escritorio)",
  "Estructura orientada a conversión (hero, beneficios, prueba social, CTA, formulario)",
  "Redacción de textos persuasivos incluida",
  "Formulario de contacto o captación de leads",
  "Integración con Google Analytics 4 y píxel (Meta / Google)",
  "Configuración SEO básica on-page",
  "Optimización de velocidad",
  "Hasta 2 rondas de revisión",
];

const needsList = [
  "El objetivo de la landing (captar leads, vender, registros, reservas…)",
  "Logo y manual de marca",
  "Imágenes o vídeos de tu producto o servicio",
  "Tu grabación del vídeo siguiendo nuestros guiones (si quieres VSL)",
  "Referencias visuales (landings que te gusten)",
  "El dominio o subdominio donde alojarla",
];

const notIncluded = [
  "Integraciones complejas (pasarelas de pago, sistemas de reservas)",
  "Versión multilingüe",
  "Gestión de la campaña de publicidad que lleva tráfico a la landing",
  "Mantenimiento posterior",
];

const steps = [
  { n: 1, title: "Contratas el servicio", desc: "Pago único de 490€ (o 3 plazos con Klarna)." },
  { n: 2, title: "Nos das tu material", desc: "Objetivo, logo, imágenes y tu grabación con nuestros guiones." },
  { n: 3, title: "Diseñamos tu landing", desc: "Estructura de conversión, textos y edición del vídeo." },
  { n: 4, title: "Revisas y ajustamos", desc: "Hasta 2 rondas de revisión." },
  { n: 5, title: "Publicamos", desc: "Tu landing lista para recibir tráfico y convertir." },
];

const LandingPageDemo = () => {
  return (
    <div className="space-y-28 overflow-hidden">
      <ServiceHero
        eyebrow="Servicio puntual · Pago único"
        title={<>Landing <Highlight>Page</Highlight></>}
        description={<>Una página diseñada para convertir. Ideal para tus campañas, lanzamientos o captación de leads. Un solo objetivo: que tu visita se convierta en cliente.</>}
        includes={["490€ (IVA incl.) · Pago único", "Financiable en 3 plazos con Klarna", "Entrega en 10-15 días"]}
        primaryCta={{ label: "Solicitar mi landing", href: "/#contact" }}
        secondaryCta={{ label: "Ver qué incluye", href: "#incluye" }}
      />

      <div id="incluye">
        <IncludesExcludes
          includes={[
            "Landing page one-page orientada a conversión",
            "Diseño adaptado a tu identidad visual + responsive",
            "Estructura de conversión (hero, beneficios, prueba social, CTA, formulario)",
            "Redacción de textos persuasivos incluida",
            "<strong>Edición de VSL o vídeo corto integrado en la landing</strong> (tú grabas con nuestros guiones, nosotros editamos)",
            "Formulario de contacto o captación de leads",
            "Integración con Analytics 4 y píxel (Meta / Google)",
            "Configuración SEO básica on-page",
            "Optimización de velocidad",
            "Hasta 2 rondas de revisión",
          ]}
          excludes={[
            "Hosting y dominio (los pones tú, o se aloja en tu web actual)",
            "Grabación del vídeo (tú grabas siguiendo nuestros guiones; nosotros editamos)",
            "Integraciones complejas (pasarelas de pago, sistemas de reservas)",
            "Multilingüe",
            "Gestión de la campaña que lleva tráfico a la landing (eso es Meta/Google Ads)",
            "Mantenimiento posterior",
          ]}
        />
      </div>

      {/* 3 COLUMNAS */}
      <section>
        <AnimateIn>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: <Package className="w-6 h-6" />, title: "Qué incluye", items: ["Landing page de conversión", "Redacción de textos incluida", "Formulario de captación", "Integración con analytics y píxel"] },
              { icon: <Calendar className="w-6 h-6" />, title: "Plazo de entrega", items: ["Entre 10 y 15 días", "Desde la recepción de tu material"] },
              { icon: <Tag className="w-6 h-6" />, title: "Inversión", items: ["490€ (IVA incluido)", "Pago único"] },
            ].map((c, i) => (
              <Card key={i} className="p-6 hover:border-primary/30 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">{c.icon}</div>
                <h4 className="font-semibold mb-3">{c.title}</h4>
                <ul className="space-y-2">
                  {c.items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 size={14} className="text-primary shrink-0 mt-0.5" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </AnimateIn>
      </section>

      {/* EJEMPLOS */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">Portfolio</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Landings que hemos creado</h3>
            <p className="text-muted-foreground">Una muestra de landing pages que hemos diseñado para las campañas de nuestros clientes.</p>
          </div>
        </AnimateIn>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { name: "Tradicioncriolla", url: "https://tradicioncriolla.es", img: "/assets/screenshots/tradicioncriolla.webp" },
            { name: "50 Juegos", url: "https://50juegos.fiestasepicas.com", img: "/assets/screenshots/50juegos.webp" },
            { name: "Habitalisimo", url: "https://habitalisimo.com", img: "/assets/screenshots/habitalisimo.webp" },
            { name: "Control GPS - Anti-Robos", url: "https://robos.controlgps.es", img: "/assets/screenshots/robos-controlgps.webp" },
            { name: "Wonder Clinic", url: "https://www.wonder.clinic/es", img: "/assets/screenshots/wonder-clinic.webp" },
            { name: "SoloPolizas", url: "https://solopolizas.com", img: "/assets/screenshots/solopolizas.webp" },
          ].map((site, idx) => (
            <AnimateIn key={idx} delay={idx * 0.06}>
              <a href={site.url} target="_blank" rel="noopener noreferrer" className="block">
                <Card className="overflow-hidden hover:border-primary/30 hover:shadow-lg transition-all group">
                  <div className="bg-muted border-b border-border">
                    <div className="h-7 flex items-center px-3 gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-destructive/60" />
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                      <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                    </div>
                  </div>
                  <div className="aspect-video overflow-hidden">
                    <img src={site.img} alt={`Captura de ${site.name}`} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <span className="font-medium text-sm">{site.name}</span>
                    <ExternalLink size={14} className="text-muted-foreground" />
                  </div>
                </Card>
              </a>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* INCLUYE */}
      <section id="incluye">
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">Alcance del servicio</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Todo lo que incluye tu landing page</h3>
            <p className="text-muted-foreground">Una página única, optimizada al máximo para que tus visitas pasen a la acción.</p>
          </div>
        </AnimateIn>
        <div className="max-w-3xl mx-auto grid sm:grid-cols-2 gap-3">
          {includesList.map((item, i) => (
            <AnimateIn key={i} delay={i * 0.03}>
              <div className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border">
                <CheckCircle2 size={18} className="text-primary shrink-0 mt-0.5" />
                <span className="text-sm">{item}</span>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* ENFOQUE CONVERSIÓN DESTACADO */}
      <section>
        <AnimateIn>
          <div className="max-w-4xl mx-auto rounded-3xl p-8 md:p-12 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border-2 border-primary/30 shadow-glow">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                <MousePointerClick className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold mb-3">Una landing tiene un solo objetivo: convertir</h3>
                <p className="text-muted-foreground mb-3">
                  A diferencia de una web, una landing page se diseña con <strong className="text-foreground">un único objetivo</strong>: que el visitante haga una acción concreta (dejar sus datos, comprar, reservar, llamar).
                </p>
                <p className="text-muted-foreground">
                  Eliminamos distracciones y construimos cada sección para guiar al usuario hacia ese objetivo. <strong className="text-foreground">Es la herramienta perfecta para tus campañas de publicidad</strong>, porque el tráfico que generas con Meta o Google aterriza en una página pensada para convertir.
                </p>
              </div>
            </div>
          </div>
        </AnimateIn>
      </section>

      {/* VSL VIDEO */}
      <section>
        <AnimateIn>
          <div className="max-w-4xl mx-auto rounded-3xl p-8 md:p-12 bg-card border-2 border-border">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <Video className="w-7 h-7" />
              </div>
              <div>
                <Badge variant="secondary" className="mb-3">Tu vídeo, editado por nosotros</Badge>
                <h3 className="text-2xl md:text-3xl font-bold mb-3">Incluye tu VSL o vídeo corto</h3>
                <p className="text-muted-foreground">
                  Te pasamos <strong className="text-foreground">guiones con estructura e indicaciones</strong> para que grabes tú mismo (a ti, tu negocio o tus productos). Nosotros lo editamos profesionalmente y lo integramos en la landing. Un vídeo bien hecho puede <strong className="text-foreground">multiplicar la conversión</strong> de tu página.
                </p>
              </div>
            </div>
          </div>
        </AnimateIn>
      </section>

      {/* QUÉ NECESITAMOS */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">Antes de empezar</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Qué necesitamos para empezar</h3>
            <p className="text-muted-foreground">Para crear tu landing y cumplir el plazo de entrega, necesitamos que nos facilites:</p>
          </div>
        </AnimateIn>
        <div className="max-w-3xl mx-auto space-y-3">
          {needsList.map((item, i) => (
            <AnimateIn key={i} delay={i * 0.04}>
              <div className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <FileText size={16} />
                </div>
                <span className="text-sm pt-1">{item}</span>
              </div>
            </AnimateIn>
          ))}
          <AnimateIn>
            <p className="text-sm text-muted-foreground text-center pt-4 italic">
              El plazo de entrega (10-15 días) empieza a contar cuando recibimos todo el material necesario.
            </p>
          </AnimateIn>
        </div>
      </section>

      {/* QUÉ NO INCLUYE */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="outline" className="mb-4 border-destructive/40 text-destructive">Sin sorpresas</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Qué no incluye este servicio</h3>
            <p className="text-muted-foreground">Para que todo quede claro desde el principio, estos puntos quedan fuera del servicio:</p>
          </div>
        </AnimateIn>
        <div className="max-w-3xl mx-auto grid gap-3">
          <AnimateIn>
            <div className="flex items-start gap-3 p-4 rounded-xl bg-destructive/5 border-2 border-destructive/30">
              <AlertTriangle size={20} className="text-destructive shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-destructive mb-1">Hosting y dominio no incluidos</div>
                <p className="text-sm text-muted-foreground">Los pones tú, o alojamos la landing en tu web actual. A diferencia de los servicios de Web Corporativa y Tienda Online, este servicio no incluye hosting ni dominio.</p>
              </div>
            </div>
          </AnimateIn>
          {notIncluded.map((item, i) => (
            <AnimateIn key={i} delay={i * 0.04}>
              <div className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border">
                <XCircle size={18} className="text-destructive shrink-0 mt-0.5" />
                <span className="text-sm text-muted-foreground">{item}</span>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* CÓMO TRABAJAMOS */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">Proceso</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Cómo trabajamos</h3>
          </div>
        </AnimateIn>
        <div className="max-w-3xl mx-auto space-y-4">
          {steps.map((s, i) => (
            <AnimateIn key={s.n} delay={i * 0.06}>
              <div className="flex items-start gap-5 p-5 rounded-2xl bg-card border border-border hover:border-primary/30 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent text-primary-foreground flex items-center justify-center font-bold text-lg shrink-0">
                  {s.n}
                </div>
                <div>
                  <h4 className="font-semibold mb-1">{s.title}</h4>
                  <p className="text-sm text-muted-foreground">{s.desc}</p>
                </div>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* CTA FINAL */}
      <section>
        <AnimateIn>
          <div className="max-w-4xl mx-auto text-center rounded-3xl p-10 md:p-16 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border-2 border-primary/30 shadow-glow">
            <h3 className="text-3xl md:text-5xl font-bold mb-4">¿Listo para convertir más con tus campañas?</h3>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Pago único de 490€, financiable en 3 plazos con Klarna. Con tu vídeo editado e integrado. Lista en 15 días.
            </p>
            <a href="/#contact" className="btn-pill bg-primary text-primary-foreground hover:shadow-glow px-8 py-4 inline-flex items-center gap-2">
              Solicitar mi landing
              <ArrowRight size={16} />
            </a>
          </div>
        </AnimateIn>
      </section>
    </div>
  );
};

export default LandingPageDemo;
